import { Grid, Theme, Box } from "@mui/material";
import { IFlight } from "@vmosolutions/oc-interfaces";
import classNames from "classnames";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { DragLayerMonitor, DropTargetMonitor, useDragLayer, useDrop } from "react-dnd";
import { DeletableFlightChip, updateRemove } from "src/components/Flights/DeletableFlightChip";
import { IFlightChip } from "src/components/Flights/FlightChip";

interface IFlightDropTargetFilterProps {
  value: IFlight[];
  onChange: (newValue: IFlight[]) => void;
  disabled?: boolean;
}

export const FlightDropTargetFilter = (props: IFlightDropTargetFilterProps): JSX.Element => {
  // Set pending position flight.
  const { value, onChange, disabled } = props;

  // Populated with default value and updated by dropzone.
  const [flightsOnDropTarget, setFlightsOnDropTarget] = useState<IFlight[]>(value);

  useEffect(() => {
    if (value?.length !== flightsOnDropTarget?.length) {
      onChange(flightsOnDropTarget);
    }
  }, [flightsOnDropTarget, onChange, value?.length]);

  /**
   * Calls remove function from deletable flight and sets returned array as contentFlights
   * @param flightId - id to search content flights
   * @param contentFlights - array of flights
   */
  const callRemove = useCallback(
    (flightId: string): void => {
      const newValue = updateRemove(flightId, flightsOnDropTarget);
      setFlightsOnDropTarget(newValue);
    },
    [flightsOnDropTarget]
  );

  // Monitor item being dragged, return as valid if type matches accepted.
  const { isValidDraggable } = useDragLayer((monitor: DragLayerMonitor) => {
    const draggedItem: any = monitor.getItem();
    return {
      isValidDraggable:
        monitor.isDragging() &&
        (draggedItem?.type === "FLIGHT" || draggedItem?.type === "SELECTED_FLIGHTS") &&
        !disabled,
    };
  });

  // HANDLE FLIGHTS if dragged over eventgroups
  const [{ isOver }, drop] = useDrop({
    //WHY! I seem to need to list "SELECTED_GROUNDEVENTS" as accepted
    //to be able to drop flights
    accept: ["FLIGHT", "SELECTED_FLIGHTS"],
    drop(item: any, monitor: DropTargetMonitor) {
      let newFlights: IFlight[] = [];

      switch (item.type) {
        case "FLIGHT":
          newFlights = [item.flight];
          break;
        case "SELECTED_FLIGHTS":
          newFlights = item.selectedFlights;
          break;
        case "SELECTED_GROUNDEVENTS":
          //left it using selectedFlights because I wasn't sure if changing the timeine
          //drag layer would cause issues else where in the app
          //newGroundEvents = item.selectedFlights;
          break;
        default:
        // code block
      }
      if (flightsOnDropTarget?.length === 0 || flightsOnDropTarget === null) {
        setFlightsOnDropTarget(newFlights);
      } else {
        const addedFlights = [...flightsOnDropTarget, ...newFlights];
        const uniqueFlight = _.uniqBy(addedFlights, _.iteratee("_id"));
        setFlightsOnDropTarget(uniqueFlight);
      }
    },

    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  const DropZoneStyles = (theme: Theme): any => ({
    event_dropzone: {
      display: "flex",
      cursor: "pointer",
      minHeight: "100px",
      justifyContent: "center",
      alignItems: "flex-start",
      flexDirection: "column",
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
      borderRadius: "3px",
      padding: "4px",
      "& .title": { fontSize: 18, fontWeight: "bold" },
      "& .body": {
        display: "flex",
        border: `2x solid ${theme.custom.forms.inputBorder}`,
        minHeight: "40px",
        alignItems: "center",
        color: theme.custom.forms.focusedColor,
        fontSize: 12,
        fontWeight: 300,
        textAlign: "center",
        padding: "4px 8px",
        opacity: 0.5,
        userSelect: "none",
      },
      "&.disabled": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.reject": {
        backgroundcolor: theme.custom.forms.errorColor,
      },
      "&.active": {
        backgroundcolor: theme.custom.forms.focusedColor,
      },
      "&.dirty": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
      "&.over": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
    },
  });

  return (
    <Box
      className={` ${isValidDraggable && "active"} ${isOver && !disabled && "over"} ${disabled && "disabled"}`}
      ref={!disabled ? drop : null}
      sx={(theme) => DropZoneStyles(theme).event_dropzone}
    >
      <div className="body">
        To filter associated flights, drag/drop them into this zone from the timeline or advanced search menu.
      </div>
      <Grid container direction="row" className={classNames("contentFlightChips")}>
        {flightsOnDropTarget?.length > 0
          ? flightsOnDropTarget.map((chipData: IFlightChip) => {
              return <DeletableFlightChip data={chipData} handleDelete={callRemove} key={`${chipData?._id}-chip`} />;
            })
          : null}
      </Grid>
    </Box>
  );
};
